import { useMemo, useState } from 'react';

import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import { useGetUniqueTags } from '@/modules/analytics/hooks/useUniqueTags';
import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { TagsMode } from '@/shared/types/analytics';

import { FilterSearchField } from './FilterSearchField';
import { TagsModeSelect } from './TagsModeSelect';
import { VirtualizedTagList } from './VirtualizedTagList';

interface AccountTagsFilterProps {
  filterIndex: number;
}

/**
 * Account-only Tags filter: simple input field where users can type tags.
 * Accepts comma or whitespace separated values and maps to `tags: string[]`.
 */
export const AccountTagsFilter = ({ filterIndex }: AccountTagsFilterProps) => {
  const { t } = useTranslation();
  const { control, getValues } = useFormContext();
  const { update } = useFieldArray({ control, name: 'filters' });
  const [searchText, setSearchText] = useState('');

  // Selected tags for this filter pill
  const watchedTags = useWatch({ control, name: `filters.${filterIndex}.tags` }) as
    string[] | undefined;
  const watchedMode = useWatch({ control, name: `filters.${filterIndex}.tagsMode` }) as
    TagsMode | undefined;
  const currentTags = useMemo<string[]>(
    () => (Array.isArray(watchedTags) ? watchedTags : []),
    [watchedTags]
  );

  // Get account analytics date range
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  // Fetch unique tags for the account within the selected date range
  const { data: allTags, isLoading, error } = useGetUniqueTags(startDate, endDate);

  const handleTagToggle = (tag: string, checked: boolean) => {
    const existing = Array.isArray(currentTags) ? [...currentTags] : [];
    const next = checked ? [...existing, tag] : existing.filter((t: string) => t !== tag);

    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, tags: next });
  };

  const handleModeChange = (tagsMode: TagsMode) => {
    const currentFilter = getValues(`filters.${filterIndex}`);
    update(filterIndex, { ...currentFilter, tagsMode });
  };

  const availableTags: string[] = useMemo(() => {
    const list = Array.isArray(allTags) ? allTags : [];
    const selected = Array.isArray(currentTags) ? currentTags : [];

    const merged = new Set<string>();

    selected.forEach((tag) => {
      if (typeof tag === 'string' && tag.trim().length > 0) {
        merged.add(tag);
      }
    });

    list.forEach((tag) => {
      if (typeof tag === 'string' && tag.trim().length > 0) {
        merged.add(tag);
      }
    });

    return Array.from(merged)
      .filter((tag) => tag.toLowerCase().includes(searchText.toLowerCase()))
      .sort((a, b) => a.localeCompare(b));
  }, [allTags, currentTags, searchText]);

  if (isLoading) {
    return (
      <div className="p-4 text-sm text-foreground-muted">{t('analytics.filters.loading_tags')}</div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-sm text-destructive">
        {t('analytics.filters.failed_to_load_tags')}
      </div>
    );
  }

  if ((availableTags?.length ?? 0) === 0 && !searchText) {
    return <FilterEmptyState text={t('analytics.filters.no_tags_available')} />;
  }

  return (
    <div className="space-y-3">
      <p className="px-2 text-xs text-foreground-muted">
        {t('analytics.filters.tags_time_scoped_hint')}
      </p>
      {currentTags.length >= 2 && (
        <TagsModeSelect value={watchedMode} onChange={handleModeChange} />
      )}
      <FilterSearchField
        value={searchText}
        onChange={setSearchText}
        placeholder={t('analytics.filters.search_tags')}
      />

      {availableTags.length === 0 && searchText ? (
        <FilterEmptyState text={t('analytics.filters.no_tags_match')} />
      ) : (
        <VirtualizedTagList
          tags={availableTags}
          selectedTags={currentTags}
          idPrefix="account-tag"
          onToggle={handleTagToggle}
          scrollResetKey={searchText}
        />
      )}
    </div>
  );
};

export default AccountTagsFilter;
